import { useId, useMemo, useState } from "react";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { extractEmails, toCsv } from "./extract";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { copyText } from "@/lib/copyText";


const TOOL_INFO_CONTENT = {
  what: `Scans a block of text and pulls out every email address it finds, useful for extracting contact lists from a document or webpage text without manually searching for each one — pasting in a page of scraped text, a forwarded email thread, or an exported contact list and getting back just the addresses, cleanly separated.`,
  how: `Paste the text you want to scan, and every detected email address is listed automatically. Copy the list or download it as a CSV.`,
  tip: `The tool only catches correctly-formatted email addresses — if a document has typos in an address, or the address is written out in plain words (like "name at domain dot com"), it won't be recognized; bracketed forms such as name [at] domain [dot] com are found only when the "Also find" option is on, and any other as a real address.`,
};

export default function EmailExtractor() {
  const idBase = useId();
  const [text, setText] = useState("");
  const [obfuscated, setObfuscated] = useState(false);
  const [sortAz, setSortAz] = useState(false);
  const [lower, setLower] = useState(false);
  const [comma, setComma] = useState(false);

  const emails = useMemo(() => {
    let list = extractEmails(text, { obfuscated });
    if (lower) list = list.map((e) => e.toLowerCase());
    if (sortAz) list = [...list].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
    return list;
  }, [text, obfuscated, sortAz, lower]);

  const copyAll = () => {
    void copyText(emails.join(comma ? ", " : "\n"), "Copied all emails to clipboard");
  };

  const downloadCsv = () => {
    downloadBlob(new Blob([toCsv(["email", "domain"], emails)], { type: "text/csv" }), "extracted-emails.csv");
  };

  const { blocked } = useLogToolUsage(true, 3000, { ownActions: true });


  return (
    <ToolContainer>
      <Textarea
        placeholder="Paste any text, email thread, or document content here — we'll pull out every email address..."
        value={text}
        onChange={e => setText(e.target.value)}
        aria-label="Text to scan"
        className="min-h-[200px]"
      />
      <div className="flex flex-wrap gap-x-5 gap-y-2 mt-3 text-sm">
        {([
          ["Also find name [at] domain [dot] com", obfuscated, setObfuscated],
          ["Sort A–Z", sortAz, setSortAz],
          ["Lowercase", lower, setLower],
          ["Copy as comma-separated", comma, setComma],
        ] as const).map(([label, val, set], i) => (
          <div key={label} className="flex items-center gap-2"><Switch id={`${idBase}-${i}`} checked={val} onCheckedChange={set} /><Label htmlFor={`${idBase}-${i}`} className="text-sm cursor-pointer">{label}</Label></div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-4 mb-2">
        <p className="text-sm text-muted-foreground">
          {blocked ? "— blocked at today's usage limit —" : `${emails.length} unique email${emails.length === 1 ? "" : "s"} found`}
        </p>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={copyAll}
            disabled={!emails.length || blocked}
          >
            Copy All
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={downloadCsv}
            disabled={!emails.length || blocked}
          >
            Download CSV
          </Button>
        </div>
      </div>
      {!blocked && emails.length > 0 && (
        <div className="rounded-lg border border-border p-4 bg-muted/30 max-h-[300px] overflow-y-auto">
          <ul className="space-y-1 font-mono text-sm">
            {emails.map(email => (
              <li key={email}>{email}</li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-3">Use extracted addresses responsibly: only contact people who have agreed to hear from you, as anti-spam laws (CAN-SPAM, GDPR) apply.</p>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
